'use client';

import { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { Mail, Phone, Clock, ListChecks, Lock } from 'lucide-react';
import Joyride from 'react-joyride';
import WorkerProfileTourSteps from '@/components/tour/worker/WorkerProfileTourSteps';
import { getTourStyles } from '@/components/tour/tourStyles';
import { motion } from 'framer-motion';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { DIAS_SEMANA } from '@/components/constants/dias';

// Hooks
import { useTrabajadorDetalles } from '@/components/hooks/useTrabajadorDetalles';
import { useAusenciasPorUsuario } from '@/components/hooks/useAusencias';
import { useDiasFestivos } from '@/components/hooks/useDiasFestivos';
import { useDiasNoLaborablesPorUsuario } from '@/components/hooks/useDiasNoLaborables';
import { useServiciosPorUsuario } from '@/components/hooks/useServicios';
import { useHorariosPorUsuario } from '@/components/hooks/useHorarios';
import {
  useTemporadas,
  buscarTemporadaVigente,
  formatearFechaCorta,
} from '@/components/hooks/useTemporadas';

// Dialogs
import WorkerHorarioDialog from '../dialogs/WorkerHorarioDialog';
import WorkerServiciosDialog from '../dialogs/WorkerServiciosDialog';
import WorkerAusenciaDialog from '../dialogs/WorkerAusenciaDialog';
import ChangePasswordDialog from '@/components/pages/dialogs/ChangePasswordDialog';
import WorkerSelfAvatar from '@/components/pages/shared/WorkerSelfAvatar';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ── Liquid Glass suave, mismo lenguaje visual que Button y dialogButtons ──
const GLASS_BTN_PRIMARY =
  "relative isolate px-4 py-2 text-sm font-semibold text-white rounded-lg " +
  "transition-all duration-200 ease-out active:scale-[0.98] " +
  "bg-[#103254] shadow-[0_1px_2px_rgba(16,50,84,0.25),inset_0_1px_0_rgba(255,255,255,0.12)] hover:bg-[#0b2138] " +
  "dark:bg-[#3d7fa0]/[0.14] dark:hover:bg-[#3d7fa0]/[0.22] " +
  "dark:supports-[backdrop-filter]:bg-[#3d7fa0]/[0.12] " +
  "dark:supports-[backdrop-filter]:backdrop-blur-xl " +
  "dark:supports-[backdrop-filter]:backdrop-saturate-150 " +
  "dark:border dark:border-[#7fb0e0]/[0.20] dark:shadow-none " +
  "before:content-[''] before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none " +
  "before:bg-[linear-gradient(180deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0)_60%)] " +
  "before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-200";

const GLASS_BTN_ICON =
  "relative isolate px-2 py-1 text-sm rounded-md " +
  "transition-all duration-200 ease-out active:scale-[0.96] " +
  "border border-black/[0.08] bg-white/[0.55] text-gray-900 shadow-[0_1px_2px_rgba(16,50,84,0.06),inset_0_1px_0_rgba(255,255,255,0.6)] " +
  "supports-[backdrop-filter]:bg-white/[0.35] supports-[backdrop-filter]:backdrop-blur-md supports-[backdrop-filter]:backdrop-saturate-150 " +
  "hover:bg-white/[0.75] hover:border-black/[0.12] " +
  "dark:border-white/[0.12] dark:bg-white/[0.05] dark:text-white dark:shadow-none " +
  "dark:supports-[backdrop-filter]:bg-white/[0.04] dark:hover:bg-white/[0.09] dark:hover:border-white/[0.18]";

const toFechaISO = (d: Date) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
    .toISOString()
    .split('T')[0];

const TITULOS_MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

export default function WorkerProfile() {
  const [loading, setLoading] = useState(true);
  const [runTour, setRunTour] = useState(false);
  const isMobile = window.innerWidth < 640;
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const [horarioOpen, setHorarioOpen] = useState(false);
  const [serviciosOpen, setServiciosOpen] = useState(false);
  const [ausenciaOpen, setAusenciaOpen] = useState(false);

  // ── Selección de rango entre calendarios (permite abarcar dos meses) ──
  const [rangoInicio, setRangoInicio] = useState<Date | null>(null);
  const [rangoSeleccionado, setRangoSeleccionado] = useState<[Date, Date] | null>(null);
  const [ausencias, setAusencias] = useState<any[]>([]);

  const userId = localStorage.getItem('id_usuario') || '';
  const empresaId = localStorage.getItem('id_empresa') || '';

  const { perfil, loading: ldPerfil } = useTrabajadorDetalles(
    userId,
    empresaId
  );
  const ausenciasHook = useAusenciasPorUsuario(userId, empresaId);
  const diasFestivos = useDiasFestivos(empresaId);
  const diasNoLaborables = useDiasNoLaborablesPorUsuario(userId, empresaId);
  const { servicios: serviciosAsignados = [] } = useServiciosPorUsuario(
    userId,
    empresaId
  );
  const { horarios: horariosRaw } = useHorariosPorUsuario(userId, empresaId);

  // Jornada de la temporada en vigor, si el trabajador tiene una propia.
  const { temporadas } = useTemporadas(empresaId);
  const temporadaVigente = buscarTemporadaVigente(temporadas);
  const { horarios: horariosTemporadaRaw } = useHorariosPorUsuario(
    userId,
    empresaId,
    temporadaVigente?.id_horario_temporada ?? null
  );

  useEffect(() => setLoading(ldPerfil), [ldPerfil]);
  useEffect(() => setAusencias(ausenciasHook), [ausenciasHook]);

  const agruparPorDia = (
    lista: { dia_semana: string; hora_inicio: string; hora_fin: string }[]
  ) =>
    lista.reduce<Record<string, { hora_inicio: string; hora_fin: string }[]>>(
      (acc, h) => {
        const d = h.dia_semana.toLowerCase();
        acc[d] ??= [];
        acc[d].push({
          hora_inicio: h.hora_inicio.slice(0, 5),
          hora_fin: h.hora_fin.slice(0, 5),
        });
        return acc;
      },
      {}
    );

  const horarios = useMemo(() => agruparPorDia(horariosRaw), [horariosRaw]);

  const horariosTemporada = useMemo(
    () => agruparPorDia(horariosTemporadaRaw),
    [horariosTemporadaRaw]
  );

  const temporadaInfo = useMemo(() => {
    if (!temporadaVigente || horariosTemporadaRaw.length === 0) return null;
    return {
      nombre: temporadaVigente.nombre,
      desde: formatearFechaCorta(temporadaVigente.fecha_desde),
      hasta: formatearFechaCorta(temporadaVigente.fecha_hasta),
    };
  }, [temporadaVigente, horariosTemporadaRaw]);

  const esRangoValido = useCallback(
    (inicio: Date, fin: Date) => {
      let cur = new Date(inicio);
      while (cur <= fin) {
        const s = toFechaISO(cur);
        if (ausencias.some((a) => a.fecha === s)) return false;
        cur.setDate(cur.getDate() + 1);
      }
      return true;
    },
    [ausencias]
  );

  // ── Click en un día: primer clic = inicio, segundo clic (en cualquier
  // mes visible) = fin. Permite seleccionar un rango que cruza meses. ──
  const handleDayClick = (date: Date) => {
    if (!rangoInicio) {
      setRangoInicio(date);
      return;
    }

    let inicio = rangoInicio;
    let fin = date;
    if (fin < inicio) [inicio, fin] = [fin, inicio];

    if (!esRangoValido(inicio, fin)) {
      toast.error('El rango contiene días no disponibles.');
      setRangoInicio(null);
      return;
    }

    setRangoSeleccionado([inicio, fin]);
    setAusenciaOpen(true);
    setRangoInicio(null);
  };

  const mesActual = new Date().getMonth();
  const añoActual = new Date().getFullYear();
  const bloqueInicial = Math.floor(mesActual / 4);
  const [bloque, setBloque] = useState(bloqueInicial);
  const [año, setAño] = useState(añoActual);

  const mesesVisibles = useMemo(() => {
    const start = bloque * 4;
    return [start, start + 1, start + 2, start + 3];
  }, [bloque]);

  const avanzarBloque = () => {
    if (bloque === 2) {
      setAño((y) => y + 1);
      setBloque(0);
    } else setBloque((b) => b + 1);
  };

  const retrocederBloque = () => {
    if (bloque === 0) {
      setAño((y) => y - 1);
      setBloque(2);
    } else setBloque((b) => b - 1);
  };

  const resumenAusencias = useMemo(() => {
    const year = String(año);
    const delAño = ausencias.filter((a) => a.fecha.startsWith(year));
    return {
      total: delAño.length,
      aprobadas: delAño.filter((a) => a.estado === 'aprobado').length,
      pendientes: delAño.filter((a) => a.estado === 'pendiente').length,
    };
  }, [ausencias, año]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-700 dark:border-gray-300" />
      </div>
    );
  }

  if (!perfil)
    return (
      <div className="p-6 text-red-600 dark:text-red-400">
        No se pudo cargar el perfil.
      </div>
    );

  return (
    <motion.div
      className="space-y-10 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* HEADER — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants} className="flex items-center gap-4 tour-title">
        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Perfil
          </h1>
        </div>
      </motion.div>

      {/* TOUR */}
      <Joyride
        steps={WorkerProfileTourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* PERFIL */}
      <motion.div variants={itemVariants}>
        <Card className={`rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-wrap justify-between items-start gap-6">
              {/* FOTO */}
              <div className="tour-foto flex items-center gap-4">
                <WorkerSelfAvatar
                  userId={userId}
                  nombre={perfil.nombre_completo}
                  foto={perfil.foto}
                  sizeClass="w-20 h-20"
                />

                <div className="tour-info-personal">
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">
                    {perfil.nombre_completo}
                  </p>
                  {perfil.email && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Mail className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                      <a
                        href={`mailto:${perfil.email}`}
                        className="hover:underline text-blue-600 dark:text-blue-400"
                      >
                        {perfil.email}
                      </a>
                    </p>
                  )}
                  {perfil.telefono && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Phone className="w-4 h-4 text-green-500 dark:text-green-400" />
                        <a
                        href={`tel:${perfil.telefono}`}
                        className="hover:underline text-blue-600 dark:text-blue-400"
                      >
                        {perfil.telefono}
                      </a>
                    </p>
                  )}
                </div>
              </div>

              {/* BOTONES */}
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button
                  className={`flex items-center justify-center gap-1.5 tour-btn-horario ${GLASS_BTN_PRIMARY}`}
                  onClick={() => setHorarioOpen(true)}
                >
                  <Clock className="w-4 h-4" />
                  Ver horario
                </button>
                <button
                  className={`flex items-center justify-center gap-1.5 tour-btn-servicios ${GLASS_BTN_PRIMARY}`}
                  onClick={() => setServiciosOpen(true)}
                >
                  <ListChecks className="w-4 h-4" />
                  Ver servicios
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* SEGURIDAD */}
      <motion.div variants={itemVariants}>
        <Card className={`rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
              <Lock className="w-5 h-5" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Seguridad
              </h3>
            </div>
            <ChangePasswordDialog />
          </CardContent>
        </Card>
      </motion.div>

      {/* CALENDARIO */}
      <motion.div variants={itemVariants}>
        <Card className={`rounded-2xl ${GLASS_CARD}`}>
          <CardContent className="p-6 space-y-6">
            <div className="tour-nav-año flex justify-end items-center gap-4">
              <button onClick={retrocederBloque} className={GLASS_BTN_ICON}>
                {'<'}
              </button>
              <div className="flex flex-col items-center leading-tight">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {año}
                </span>
              </div>
              <button onClick={avanzarBloque} className={GLASS_BTN_ICON}>
                {'>'}
              </button>
            </div>

            {/* LEYENDA + RESUMEN */}
            <div className="tour-leyenda flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-x-2 flex-wrap">
                  <Legend
                    bgClass="bg-red-100 dark:bg-red-900/30"
                    label="No lab."
                  />
                  <Legend
                    bgClass="bg-gray-200 dark:bg-white/10"
                    label="Festivo"
                  />
                  <Legend
                    bgClass="bg-purple-200 dark:bg-purple-900/30"
                    label="Aprobada"
                  />
                  <Legend
                    bgClass="bg-yellow-100 dark:bg-yellow-900/30"
                    label="Pendiente"
                  />
                  <Legend
                    bgClass="bg-[#103254]/20 dark:bg-blue-400/20"
                    label="Inicio elegido"
                  />
                </div>

                {resumenAusencias.total > 0 && (
                  <div
                    className={`hidden sm:flex items-center gap-2 shrink-0 text-xs text-gray-500 dark:text-gray-400 ${
                      !isMobile ? 'tour-resumen-ausencias' : ''
                    }`}
                  >
                    <span>{resumenAusencias.total} días</span>
                    {resumenAusencias.aprobadas > 0 && (
                      <span className="text-purple-600 dark:text-purple-400">
                        {resumenAusencias.aprobadas} apr.
                      </span>
                    )}
                    {resumenAusencias.pendientes > 0 && (
                      <span className="text-yellow-600 dark:text-yellow-400">
                        {resumenAusencias.pendientes} pend.
                      </span>
                    )}
                  </div>
                )}
              </div>

              {resumenAusencias.total > 0 && (
                <div
                  className={`sm:hidden flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 ${
                    isMobile ? 'tour-resumen-ausencias' : ''
                  }`}
                >
                  <span>{resumenAusencias.total} días</span>
                  {resumenAusencias.aprobadas > 0 && (
                    <span className="text-purple-600 dark:text-purple-400">
                      {resumenAusencias.aprobadas} apr.
                    </span>
                  )}
                  {resumenAusencias.pendientes > 0 && (
                    <span className="text-yellow-600 dark:text-yellow-400">
                      {resumenAusencias.pendientes} pend.
                    </span>
                  )}
                </div>
              )}

              {rangoInicio && (
                <p className="text-xs font-medium text-[#103254] dark:text-blue-400">
                  Inicio seleccionado: {toFechaISO(rangoInicio)} — pulsa el día
                  final (puede estar en otro mes), o el mismo día para una
                  ausencia de un solo día.
                  <button
                    onClick={() => setRangoInicio(null)}
                    className="ml-2 underline hover:no-underline"
                  >
                    Cancelar
                  </button>
                </p>
              )}
            </div>

            <div className="tour-calendario grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {mesesVisibles.map((m) => {
                const start = new Date(año, m, 1);
                return (
                  <div
                    key={m}
                    className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl shadow-sm p-3"
                  >
                    <h3 className="text-center text-[#103254] dark:text-blue-400 font-semibold text-lg pb-2 border-b-2 border-[#103254] dark:border-blue-400">
                      {TITULOS_MESES[m]}
                    </h3>
                    <Calendar
                      value={null}
                      activeStartDate={start}
                      onClickDay={(date) => handleDayClick(date)}
                      tileClassName={({ date }) => {
                        const iso = toFechaISO(date);
                        if (rangoInicio && iso === toFechaISO(rangoInicio))
                          return 'bg-[#103254]/20 dark:bg-blue-400/20 text-[#103254] dark:text-blue-300 font-semibold';
                        if (diasFestivos.includes(iso))
                          return 'bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300';
                        const aus = ausencias.find((a) => a.fecha === iso);
                        if (aus)
                          return aus.estado === 'pendiente'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                            : 'bg-purple-200 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
                        if (diasNoLaborables.includes(iso))
                          return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
                        return '';
                      }}
                      showNeighboringMonth={false}
                      prevLabel={null}
                      nextLabel={null}
                      prev2Label={null}
                      next2Label={null}
                      minDetail="month"
                      maxDetail="month"
                    />
                  </div>
                );
              })}
            </div>

            <div className="tour-solicitar-ausencia flex justify-end">
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                Pulsa un día de inicio y otro de fin (pueden estar en meses
                distintos) para solicitar una ausencia.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* DIALOGS */}
      <WorkerHorarioDialog
        open={horarioOpen}
        onOpenChange={setHorarioOpen}
        horarios={horarios}
        horariosTemporada={horariosTemporada}
        temporada={temporadaInfo}
        diasSemana={DIAS_SEMANA}
      />
      <WorkerServiciosDialog
        open={serviciosOpen}
        onOpenChange={setServiciosOpen}
        servicios={serviciosAsignados}
      />
      <WorkerAusenciaDialog
        open={ausenciaOpen}
        onOpenChange={setAusenciaOpen}
        empresaId={empresaId}
        userId={userId}
        rangoSeleccionado={rangoSeleccionado}
        diasFestivos={diasFestivos}
        diasNoLaborables={diasNoLaborables}
        onNuevaAusencia={(n) => setAusencias((prev) => [...prev, ...n])}
      />
    </motion.div>
  );
}

function Legend({ bgClass, label }: { bgClass: string; label: string }) {
  return (
    <div className="flex items-center gap-1 shrink-0">
      <div
        className={`w-3 h-3 shrink-0 rounded-sm border border-gray-300 dark:border-white/20 ${bgClass}`}
      />
      <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {label}
      </span>
    </div>
  );
}